'use client';

import type { Id } from '@/convex/_generated/dataModel';
import type { OrgChatSelectedConnector } from '@/components/org-chat/org-chat-types';
import {
  isProductPresetSubtypeSelection,
  type ProductPresetSubtypeSelection,
} from '@/lib/product-preset-subtypes';
import {
  parseStoredOnboardingIntent,
  type OnboardingIntent,
} from '@/lib/onboarding-intent';
import React, { createContext, useContext, useEffect, useState } from 'react';

export type { OnboardingIntent } from '@/lib/onboarding-intent';

export type ThreadAutoSend = {
  targetThreadId: string;
  orgSlug: string;
  prompt: string;
  rawUserPrompt?: string;
  attachments?: Array<{
    filename: string;
    mimeType: string;
    sizeBytes: number;
    storageKey?: string;
    url?: string;
    storageId?: Id<'_storage'>;
  }>;
  presetId?: Id<'globalProductPresets'> | null;
  productPresetId?: Id<'globalProductPresets'> | null;
  productPresetSubtypeSelections?: Array<
    ProductPresetSubtypeSelection<Id<'globalProductPresets'>>
  >;
  stylePresetId?: Id<'globalStylePresets'> | null;
  colorPresetId?: Id<'globalColorPresets'> | null;
  selectedColorPresetIds?: Array<Id<'globalColorPresets'>>;
  selectedColorPalette?: Array<string>;
  selectedConnectors?: Array<OrgChatSelectedConnector>;
  additionalInstructions?: string;
  forceDeepResearch?: boolean;
  fixedModelAlias?: string;
  productReferences?: Array<{
    filename: string;
    mimeType: string;
    sizeBytes: number;
    storageKey?: string;
    url?: string;
    storageId?: Id<'_storage'>;
  }>;
  styleReferences?: Array<{
    filename: string;
    mimeType: string;
    sizeBytes: number;
    storageKey?: string;
    url?: string;
    storageId?: Id<'_storage'>;
  }>;
  colorReferences?: Array<{
    filename: string;
    mimeType: string;
    sizeBytes: number;
    storageKey?: string;
    url?: string;
    storageId?: Id<'_storage'>;
  }>;
};

export type OnboardingContextValue = {
  intent: OnboardingIntent | null;
  setIntent: (intent: OnboardingIntent) => void;
  clear: () => void;
  threadAutoSend: ThreadAutoSend | null;
  setThreadAutoSend: (data: ThreadAutoSend | null) => void;
  clearThreadAutoSend: () => void;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStoredThreadMediaReference(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return (
    typeof value.filename === 'string' &&
    typeof value.mimeType === 'string' &&
    typeof value.sizeBytes === 'number' &&
    Number.isFinite(value.sizeBytes) &&
    value.sizeBytes >= 0 &&
    [value.storageKey, value.url, value.storageId].every(
      (field) => field === undefined || typeof field === 'string'
    )
  );
}

function parseStoredThreadAutoSend(value: unknown): ThreadAutoSend | null {
  if (
    !isRecord(value) ||
    typeof value.targetThreadId !== 'string' ||
    typeof value.orgSlug !== 'string' ||
    typeof value.prompt !== 'string'
  ) {
    return null;
  }
  for (const key of [
    'attachments',
    'productReferences',
    'styleReferences',
    'colorReferences',
  ] as const) {
    const references = value[key];
    if (
      references !== undefined &&
      (!Array.isArray(references) ||
        !references.every(isStoredThreadMediaReference))
    ) {
      return null;
    }
  }
  if (
    value.selectedColorPalette !== undefined &&
    (!Array.isArray(value.selectedColorPalette) ||
      !value.selectedColorPalette.every((item) => typeof item === 'string'))
  ) {
    return null;
  }
  if (
    value.productPresetSubtypeSelections !== undefined &&
    (!Array.isArray(value.productPresetSubtypeSelections) ||
      !value.productPresetSubtypeSelections.every(
        isProductPresetSubtypeSelection
      ))
  ) {
    return null;
  }
  const selectedConnectors = value.selectedConnectors ?? value.selectedPlugins;
  return {
    ...value,
    ...(Array.isArray(selectedConnectors) ? { selectedConnectors } : {}),
  } as ThreadAutoSend;
}

const OnboardingContext = createContext<OnboardingContextValue | undefined>(
  undefined
);

const STORAGE_KEY = 'onboarding.intent';
const THREAD_AUTO_SEND_KEY = 'onboarding.threadAutoSend';

function readInitialThreadAutoSend(): ThreadAutoSend | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(THREAD_AUTO_SEND_KEY);
    if (!stored) return null;
    const parsed = parseStoredThreadAutoSend(JSON.parse(stored));
    if (!parsed) localStorage.removeItem(THREAD_AUTO_SEND_KEY);
    return parsed;
  } catch {
    try {
      localStorage.removeItem(THREAD_AUTO_SEND_KEY);
    } catch {
      // Browser storage itself may be unavailable.
    }
    return null;
  }
}

export function OnboardingProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [intent, setIntentState] = useState<OnboardingIntent | null>(null);
  const [threadAutoSend, setThreadAutoSendState] =
    useState<ThreadAutoSend | null>(readInitialThreadAutoSend);

  // Hydrate from localStorage on mount
  useEffect(() => {
    try {
      // Try localStorage first
      let stored = localStorage.getItem(STORAGE_KEY);
      // Fallback to sessionStorage if localStorage is empty
      if (!stored) {
        stored = sessionStorage.getItem(STORAGE_KEY);
        if (stored) {
          console.log('[onboarding] Restoring intent from sessionStorage');
        }
      }
      if (stored) {
        const parsed = parseStoredOnboardingIntent(JSON.parse(stored));
        if (parsed) {
          setIntentState(parsed);
          console.log(
            '[onboarding] Intent restored, category:',
            parsed.category,
            'sourceType:',
            parsed.sourceType
          );
        } else {
          localStorage.removeItem(STORAGE_KEY);
          sessionStorage.removeItem(STORAGE_KEY);
          console.warn('[onboarding] Ignored invalid stored intent');
        }
      }
    } catch (err) {
      console.error('[onboarding] Failed to hydrate intent from storage:', err);
      try {
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        // Browser storage itself may be unavailable.
      }
    }
  }, []);

  const setIntent = (newIntent: OnboardingIntent) => {
    setIntentState(newIntent);
    try {
      const serialized = JSON.stringify(newIntent);
      // Check size before storing (localStorage limit is typically 5-10MB)
      if (serialized.length > 4_000_000) {
        console.warn(
          '[onboarding] Intent data too large for localStorage, attempting to store anyway'
        );
      }
      localStorage.setItem(STORAGE_KEY, serialized);
      // Verify it was stored
      const verify = localStorage.getItem(STORAGE_KEY);
      if (!verify) {
        throw new Error('Failed to verify localStorage write');
      }
      console.log(
        '[onboarding] Intent stored successfully, size:',
        serialized.length,
        'bytes'
      );
    } catch (err) {
      console.error(
        '[onboarding] Failed to store intent in localStorage:',
        err
      );
      // Try sessionStorage as fallback
      try {
        const serialized = JSON.stringify(newIntent);
        sessionStorage.setItem(STORAGE_KEY, serialized);
        console.log('[onboarding] Intent stored in sessionStorage as fallback');
      } catch (sessionErr) {
        console.error(
          '[onboarding] Failed to store intent in sessionStorage:',
          sessionErr
        );
      }
    }
  };

  const clear = () => {
    setIntentState(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore storage errors
    }
  };

  const setThreadAutoSend = (data: ThreadAutoSend | null) => {
    setThreadAutoSendState(data);
    try {
      if (data) {
        localStorage.setItem(THREAD_AUTO_SEND_KEY, JSON.stringify(data));
      } else {
        localStorage.removeItem(THREAD_AUTO_SEND_KEY);
      }
    } catch {
      // ignore storage errors
    }
  };

  const clearThreadAutoSend = () => {
    setThreadAutoSendState(null);
    try {
      localStorage.removeItem(THREAD_AUTO_SEND_KEY);
    } catch {
      // ignore storage errors
    }
  };

  // NOTE: We intentionally render children immediately (no isHydrated gate).
  // `intent`/`threadAutoSend` start as null on both the server and the initial
  // client render, so there is no hydration mismatch; localStorage values load
  // in the effect above and update state post-hydration. Gating children on a
  // client-only "hydrated" flag here previously returned null during SSR, which
  // suppressed server-rendered HTML for the ENTIRE app (this provider wraps the
  // whole tree in the root layout) — breaking SEO/GEO and first paint.
  return (
    <OnboardingContext.Provider
      value={{
        intent,
        setIntent,
        clear,
        threadAutoSend,
        setThreadAutoSend,
        clearThreadAutoSend,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding(): OnboardingContextValue {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}
